import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../context/AuthContext';
import { workOrderApi } from '../../api/workOrderApi';
import FileAttachment from '../../components/FileAttachment/FileAttachment';
import WorkOrderOpinionModal from './WorkOrderOpinionModal';
import WorkOrderFormModal from './WorkOrderFormModal';
import WorkOrderReportFormModal from './WorkOrderReportFormModal';
import './WorkOrderFormModal.css';

function WorkOrderReportDetailModal({
  open = true,
  workOrderId,
  workOrder,
  report,
  isAuthor = false, // 지시자 여부
  currentUserId,
  onClose,
  onReviewed, // 승인/반려 완료 콜백
  onStartEditReport // 보완 수정 클릭 시
}) {
  const { user } = useContext(AuthContext);
  const [workOrderDetail, setWorkOrderDetail] = useState(workOrder || null);
  const [isEditing, setIsEditing] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showOpinionViewModal, setShowOpinionViewModal] = useState(false);
  const [viewWorkOrderOpen, setViewWorkOrderOpen] = useState(false);

  const [currentReport, setCurrentReport] = useState(report || null);

  useEffect(() => {
    setCurrentReport(report);
  }, [report]);

  useEffect(() => {
    if (workOrder) {
      setWorkOrderDetail(workOrder);
    }
  }, [workOrder]);

  useEffect(() => {
    const targetId = workOrderId || report?.work_order_id;
    if (open && targetId) {
      workOrderApi.getDetail(targetId).then(res => {
        if (res && res.data) {
          setWorkOrderDetail(res.data);
          if (res.data.reports && report?.id) {
            const freshReport = res.data.reports.find(r => r.id === report.id);
            if (freshReport) {
              setCurrentReport(freshReport);
            }
          }
        }
      }).catch(err => console.error('[WorkOrderReportDetailModal] load detail err:', err));
    }
  }, [open, workOrderId, report?.id, report?.work_order_id]);

  if (!open || !currentReport) return null;

  const activeReport = currentReport || report;
  const currentWorkOrder = workOrderDetail || workOrder;
  const currentUserIdStr = String(currentUserId || user?.id || user?.userid || '');
  const reporterIdStr = String(activeReport.user_id || activeReport.reporter?.id || activeReport.reporter?.userid || '');
  const isMyReport = currentUserIdStr === reporterIdStr;

  const isSuperAdmin = user?.is_admin === true || user?.is_admin === 1 || user?.role === 'admin';
  const isOrderAuthor = isAuthor || (currentWorkOrder && String(currentWorkOrder.author_id) === currentUserIdStr);
  const canReview = (isOrderAuthor || isSuperAdmin) && ['submitted', 'pending', 'review'].includes(activeReport.approval_status);

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr).substring(0, 10);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return String(dateStr).substring(0, 10);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return (
          <span
            className="badge d-inline-flex align-items-center gap-1.5 shadow-sm fw-bold"
            style={{
              backgroundColor: '#10b981',
              color: '#ffffff',
              fontSize: '0.78rem',
              padding: '4px 10px',
              borderRadius: '6px'
            }}
          >
            <i className="fas fa-check-circle" />
            확인완료
          </span>
        );
      case 'rejected':
      case 'reject':
        return (
          <span
            className="badge d-inline-flex align-items-center gap-1.5 shadow-sm fw-bold"
            style={{
              backgroundColor: '#ef4444',
              color: '#ffffff',
              fontSize: '0.78rem',
              padding: '4px 10px',
              borderRadius: '6px'
            }}
          >
            <i className="fas fa-exclamation-circle" />
            보완요청
          </span>
        );
      case 'draft':
      case 'temp':
        return (
          <span
            className="badge d-inline-flex align-items-center gap-1.5 shadow-sm fw-bold"
            style={{
              backgroundColor: '#64748b',
              color: '#ffffff',
              fontSize: '0.78rem',
              padding: '4px 10px',
              borderRadius: '6px'
            }}
          >
            <i className="fas fa-file-alt" />
            임시저장
          </span>
        );
      case 'submitted':
      case 'review':
      default:
        return (
          <span
            className="badge d-inline-flex align-items-center gap-1.5 shadow-sm fw-bold"
            style={{
              backgroundColor: '#f59e0b',
              color: '#ffffff',
              fontSize: '0.78rem',
              padding: '4px 10px',
              borderRadius: '6px'
            }}
          >
            <i className="fas fa-clock" />
            검토대기
          </span>
        );
    }
  };

  // 확인완료 처리 (모달 팝업 연동)
  const handleApproveWithOpinion = async (commentText) => {
    try {
      setReviewLoading(true);
      const res = await workOrderApi.reviewReport(workOrderId || report.work_order_id, report.id, {
        approval_status: 'approved',
        review_comment: commentText?.trim() || '처리결과를 최종 확인완료하였습니다.'
      });
      if (res.success) {
        alert(res.message || '처리결과가 성공적으로 확인완료되었습니다.');
        setShowApproveModal(false);
        if (onReviewed) onReviewed();
        onClose();
      } else {
        alert(res.message || '확인완료 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('[WorkOrderReportDetailModal] approve error:', err);
      alert(err.response?.data?.message || err.message || '확인완료 처리 중 오류가 발생했습니다.');
    } finally {
      setReviewLoading(false);
    }
  };

  // 보완요청 처리 (사유 모달 연동)
  const handleRejectWithOpinion = async (reasonText) => {
    try {
      setReviewLoading(true);
      const res = await workOrderApi.reviewReport(workOrderId, report.id, {
        approval_status: 'rejected',
        review_comment: reasonText
      });
      if (res.success) {
        alert(res.message || '보완요청이 전달되었습니다.');
        setShowRejectModal(false);
        if (onReviewed) onReviewed();
        onClose();
      } else {
        alert(res.message || '보완요청 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('[WorkOrderReportDetailModal] reject error:', err);
      alert(err.response?.data?.message || err.message || '보완요청 처리 중 오류가 발생했습니다.');
    } finally {
      setReviewLoading(false);
    }
  };

  const isPrivate = activeReport.is_public === false || activeReport.is_public === 0;
  const isMasked = activeReport.report_content?.includes('🔒');

  const rUserId = String(
    activeReport.author_id ||
    activeReport.author?.id ||
    activeReport.author?.userid ||
    activeReport.user_id ||
    activeReport.reporter?.id ||
    activeReport.reporter?.userid ||
    ''
  );
  const rAuthorName = activeReport.author?.name || activeReport.author_name || '';
  const targetAssignee = activeReport.assignee || currentWorkOrder?.assignees?.find(a => {
    const aUserId = String(a.user_id || a.user?.id || a.user?.userid || '');
    const aName = a.user?.name || a.name || '';
    if (activeReport.assignee_id && a.id === activeReport.assignee_id) return true;
    if (aUserId && rUserId && aUserId === rUserId) return true;
    if (rAuthorName && aName && rAuthorName === aName) return true;
    return false;
  }) || (currentWorkOrder?.assignees?.length === 1 ? currentWorkOrder.assignees[0] : null);

  const matchedProgress = activeReport.progress_rate !== undefined && activeReport.progress_rate !== null
    ? activeReport.progress_rate
    : (targetAssignee?.progress_rate !== undefined && targetAssignee?.progress_rate !== null
        ? targetAssignee.progress_rate
        : (activeReport.progress ?? activeReport.progressRate ?? 0));

  const resolvedTitle = currentWorkOrder?.title || activeReport.work_order?.title || activeReport.workOrder?.title || activeReport.work_order_title || '';

  const startDateStr = formatDateOnly(currentWorkOrder?.start_date);
  const dueDateStr = formatDateOnly(currentWorkOrder?.due_date);
  const periodText = (startDateStr !== '-' && dueDateStr !== '-')
    ? `${startDateStr} ~ ${dueDateStr}`
    : (startDateStr !== '-' ? startDateStr : (dueDateStr !== '-' ? dueDateStr : '-'));

  const rawReportTitle = activeReport.report_title || '';
  const pureReportTitle = rawReportTitle.replace(/^(\[\d+차\s*(?:결과)?보고\]|\[최종\s*(?:결과)?보고\]|\[\d+차\s*결과\]|\[최종결과\]|\[\d+차\s*보완\]|\[최종\s*보완\])\s*/, '');
  const displayReportTitle = pureReportTitle || rawReportTitle || `${activeReport.report_seq || 1}차 처리결과`;
  const isFinal = Boolean(activeReport.is_final || Number(activeReport.progress_rate) === 100);

  if (isEditing) {
    return (
      <WorkOrderReportFormModal
        open={true}
        isSubModal={true}
        workOrderId={workOrderId || activeReport.work_order_id}
        workOrder={currentWorkOrder}
        workOrderTitle={resolvedTitle}
        currentProgress={matchedProgress}
        report={activeReport}
        reportSeq={activeReport?.report_seq || 1}
        onClose={() => setIsEditing(false)}
        onSubmitted={() => {
          setIsEditing(false);
          if (onReviewed) onReviewed();
          onClose();
        }}
      />
    );
  }

  const modalContent = (
    <div
      className="wo-modal-overlay"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.25)', zIndex: 10010 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="wo-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="wo-modal-content">

          {/* ── 헤더 (녹색/청록색 테마 및 우측 메타바) ── */}
          <div
            className="wo-modal-header"
            style={{
              background: 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)',
              padding: '16px 24px'
            }}
          >
            <h5
              className="m-0 fw-bold fs-5 text-white d-flex align-items-center gap-2 text-truncate"
              style={{ minWidth: 0 }}
              title={activeReport.report_title || `${activeReport.report_seq || 1}차 처리결과`}
            >
              <i className="fas fa-file-alt flex-shrink-0" />
              {Boolean(activeReport.is_final || Number(activeReport.progress_rate) === 100) ? (
                <span className="badge bg-white fw-bold shadow-sm flex-shrink-0" style={{ color: '#0f766e', fontSize: '0.74rem' }}>
                  최종결과
                </span>
              ) : (
                <span className="badge bg-white fw-bold shadow-sm flex-shrink-0" style={{ color: '#0f766e', fontSize: '0.74rem' }}>
                  {activeReport.report_seq || 1}차 결과
                </span>
              )}
              <span className="text-truncate">{activeReport.report_title || `${activeReport.report_seq || 1}차 처리결과`}</span>
            </h5>

            <div className="d-flex align-items-center gap-3 flex-shrink-0 ms-3">
              <div
                className="d-flex align-items-center gap-3"
                style={{
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.88)',
                  paddingRight: '14px',
                  borderRight: '1px solid rgba(255,255,255,0.25)',
                  marginTop: '1px'
                }}
              >
                <span>
                  <i className="fas fa-user-edit me-1 text-info" />
                  담당자: <strong>
                    {activeReport.reporter?.name || activeReport.reporter_name || activeReport.user?.name || '담당자'}
                    {activeReport.reporter?.org?.position ? ` ${activeReport.reporter.org.position}` : ''}
                    {activeReport.reporter?.org?.dept_name ? ` (${activeReport.reporter.org.dept_name})` : ''}
                  </strong>
                </span>
                <span>
                  <i className="fas fa-calendar-alt me-1 text-warning" />
                  등록일: <strong>{formatDateOnly(activeReport.submitted_at || activeReport.created_at)}</strong>
                </span>
              </div>

              {getStatusBadge(activeReport.approval_status)}
              <button type="button" className="btn-close btn-close-white ms-1" onClick={onClose} style={{ boxShadow: 'none' }} />
            </div>
          </div>

          {/* ── 본문 ── */}
          <div className="wo-modal-body p-3" style={{ height: 'auto', maxHeight: '78vh', backgroundColor: '#f8fafc', overflowY: 'auto' }}>
            <div className="d-flex flex-column gap-3">

              {/* 1. 상단 원문서 정보 바 (단독 행) */}
              <div className="p-3 bg-white rounded-3 shadow-sm border d-flex align-items-center" style={{ borderColor: '#e2e8f0', fontSize: '0.85rem' }}>
                <div className="d-flex align-items-center gap-2 text-truncate w-100" style={{ minWidth: 0 }}>
                  <i className="fas fa-file-alt flex-shrink-0" style={{ color: '#0d9488', fontSize: '0.95rem' }} />
                  <span className="text-secondary fw-bold flex-shrink-0">요청원문서 : </span>
                  <span
                    className="fw-bold text-truncate"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#2563eb';
                      e.currentTarget.style.textDecoration = 'underline';
                      if (e.currentTarget.scrollWidth > e.currentTarget.clientWidth) {
                        e.currentTarget.title = resolvedTitle || '원문 업무협조';
                      } else {
                        e.currentTarget.removeAttribute('title');
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#1e40af';
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                    onClick={() => setViewWorkOrderOpen(true)}
                    style={{
                      color: '#1e40af',
                      cursor: 'pointer',
                      transition: 'color 0.15s ease'
                    }}
                  >
                    {resolvedTitle || '원문 업무협조'}
                  </span>
                </div>
              </div>

              {/* 2. 처리결과 제목 (8) & 업무 기간 (4) */}
              <div className="row g-3">
                {/* 좌측: 처리결과 제목 (col-12 col-md-8) */}
                <div className="col-12 col-md-8">
                  <div className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex flex-column justify-content-center" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-heading me-1 text-teal" style={{ color: '#0d9488' }} /> 처리결과 제목
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge fw-bold flex-shrink-0"
                        style={{
                          fontSize: '0.78rem',
                          padding: '4px 8px',
                          backgroundColor: isFinal ? '#ecfdf5' : '#f8fafc',
                          color: isFinal ? '#047857' : '#334155',
                          border: `1px solid ${isFinal ? '#a7f3d0' : '#cbd5e1'}`
                        }}
                      >
                        {isFinal ? '[최종결과]' : `[${activeReport.report_seq || 1}차 결과]`}
                      </span>
                      <span className="fw-bold text-truncate" style={{ fontSize: '0.85rem', color: '#1e293b' }} title={displayReportTitle}>
                        {displayReportTitle}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 우측: 업무 기간 (col-12 col-md-4) */}
                <div className="col-12 col-md-4">
                  <div className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex flex-column justify-content-center" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-calendar-alt me-1 text-teal" style={{ color: '#0d9488' }} /> 업무 기간
                    </label>
                    <div className="fw-bold" style={{ fontSize: '0.85rem', color: '#1e293b' }}>
                      {periodText}
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. 진척도 (6) & 공개 여부 (6) 2열 카드 (50:50 반반 분할) */}
              {(() => {
                const rate = Math.min(100, Math.max(0, Number(matchedProgress) || 0));
                const is100 = rate === 100;
                const isApproved = ['approved', 'completed', 'done'].includes(activeReport.approval_status);
                const activeColor = isApproved ? (is100 ? '#10b981' : '#2563eb') : '#6366f1';
                const activeBg = isApproved ? (is100 ? '#ecfdf5' : '#eff6ff') : '#eef2ff';
                const activeBorder = isApproved ? (is100 ? '#a7f3d0' : '#bfdbfe') : '#c7d2fe';

                return (
                  <div className="row g-3">
                    {/* 좌측: 진척도 (col-12 col-md-6, 50% 확장) */}
                    <div className="col-12 col-md-6">
                      <div className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex flex-column justify-content-center" style={{ borderColor: '#e2e8f0' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-chart-line me-1 text-teal" style={{ color: '#0d9488' }} /> 진척도
                            {!isApproved && (
                              <span className="ms-1 fw-normal text-muted" style={{ fontSize: '0.74rem' }}>
                                (검토대기)
                              </span>
                            )}
                          </label>
                          <div
                            className="d-flex align-items-center justify-content-center fw-bold rounded px-2"
                            style={{
                              fontSize: '0.82rem',
                              height: '24px',
                              backgroundColor: activeBg,
                              color: activeColor,
                              border: `1px solid ${activeBorder}`
                            }}
                          >
                            {rate}%
                          </div>
                        </div>
                        <div className="progress" style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            className={`progress-bar ${!isApproved ? 'progress-bar-striped progress-bar-animated' : ''}`}
                            role="progressbar"
                            style={{
                              width: `${Math.min(100, Math.max(0, rate))}%`,
                              backgroundColor: activeColor,
                              opacity: isApproved ? 1 : 0.85,
                              transition: 'width 0.4s ease'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 우측: 공개 여부 (col-12 col-md-6) */}
                    <div className="col-12 col-md-6">
                      <div className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex flex-column justify-content-center" style={{ borderColor: isPrivate ? '#fde68a' : '#e2e8f0' }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <label className="form-label fw-bold mb-0 text-truncate" style={{ fontSize: '0.88rem', color: isPrivate ? '#b45309' : '#1e293b' }}>
                            {isPrivate ? '🔒 비공개 처리결과' : '🌐 담당자 및 참조자에게 처리결과 전체 공개'}
                          </label>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: isPrivate ? '#d97706' : '#64748b' }}>
                          {isPrivate
                            ? '요청자와 작성자만 내용 및 첨부파일 열람 가능'
                            : '모든 담당자와 참조자가 내용 및 첨부파일 열람 가능'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}



              {/* 3. 보고서 본문 카드 (첨부파일 유무에 따라 340px / 430px 자동 조절) */}
              {(() => {
                const hasAttachments = Array.isArray(activeReport.files) && activeReport.files.length > 0;
                const minHeight = hasAttachments ? '340px' : '430px';

                return (
                  <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-align-left me-1" style={{ color: '#0d9488' }} /> 처리결과 상세 내용
                    </label>
                    {isMasked ? (
                      <div className="p-4 text-center text-muted bg-light rounded-2 border">
                        <i className="fas fa-lock fs-3 mb-2 text-warning d-block" />
                        <strong>작성자와 요청자만 열람할 수 있는 비공개 처리결과입니다.</strong>
                      </div>
                    ) : (
                      <div className="bg-light rounded-2 p-1 border">
                        <div
                          className="bg-white p-3 rounded shadow-sm overflow-auto ProseMirror wo-content-view"
                          style={{ minHeight, fontSize: '14px', lineHeight: '1.6' }}
                          dangerouslySetInnerHTML={{ __html: activeReport.report_content || '<p class="text-muted">(작성된 내용이 없습니다.)</p>' }}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 4. 산출물 및 첨부파일 카드 (첨부파일이 1개 이상 존재할 때만 노출) */}
              {Array.isArray(activeReport.files) && activeReport.files.length > 0 && (
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.85rem' }}>
                    <span>
                      <i className="fas fa-paperclip me-1" style={{ color: '#0d9488' }} /> 산출물 및 첨부파일
                    </span>
                    <span className="badge bg-secondary" style={{ fontSize: '0.72rem' }}>
                      총 {activeReport.files.length}개
                    </span>
                  </label>
                  <div className="bg-light rounded-2 p-1 border">
                    <FileAttachment
                      files={activeReport.files.map(f => ({
                        id: f.id,
                        name: f.file_name || f.originalName || f.name,
                        originalName: f.file_name || f.originalName || f.name,
                        size: f.file_size ?? f.size ?? 0,
                        path: f.file_path || f.path || '',
                        isNew: false
                      }))}
                      mode="view"
                    />
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* ── 푸터 ── */}
          <div className="wo-modal-footer">
            {/* 맨 왼쪽: 업무지시 조회 버튼 */}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm px-3 fw-bold me-auto shadow-sm"
              onClick={() => setViewWorkOrderOpen(true)}
              title="원문 업무협조 내용을 조회합니다."
            >
              <i className="fas fa-file-alt me-1" />
              업무협조 조회
            </button>

            {/* 의견확인 버튼: 작성자, 지시자, 관리자에게만 노출 (참조자 및 제3자 미노출) */}
            {(isMyReport || isOrderAuthor || isSuperAdmin) && (activeReport.approval_status === 'rejected' || (activeReport.opinions && activeReport.opinions.length > 0) || activeReport.review_comment) && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-3 fw-bold"
                onClick={() => setShowOpinionViewModal(true)}
              >
                <i className="fas fa-comment-dots me-1 text-primary" />
                의견확인
              </button>
            )}

            {/* 요청자 검토 액션 버튼 (검토대기 상태일 때) */}
            {canReview && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm px-3 fw-bold"
                  onClick={() => setShowRejectModal(true)}
                  disabled={reviewLoading}
                >
                  <i className="fas fa-times me-1" />
                  보완요청
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm px-4 fw-bold"
                  onClick={() => setShowApproveModal(true)}
                  disabled={reviewLoading}
                >
                  <i className="fas fa-check me-1" />
                  {Boolean(activeReport.is_final || Number(activeReport.progress_rate) === 100)
                    ? '최종 확인완료 (업무 완료)'
                    : '확인완료 (진행 유지)'}
                </button>
              </>
            )}

            {/* 작성자/관리자 반려 시 보완 수정 버튼 */}
            {(isMyReport || isSuperAdmin) && (activeReport.approval_status === 'rejected' || activeReport.approval_status === 'reject') && (
              <button
                type="button"
                className="btn btn-warning btn-sm px-3 fw-bold text-dark"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit me-1" />
                보완 수정
              </button>
            )}

            {/* 작성자/관리자 임시저장 시 수정/이어작성 버튼 */}
            {(isMyReport || isSuperAdmin) && (activeReport.approval_status === 'draft' || activeReport.approval_status === 'temp') && (
              <button
                type="button"
                className="btn btn-warning btn-sm px-3 fw-bold text-dark"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit me-1" />
                수정 / 이어작성
              </button>
            )}

            <button type="button" className="btn btn-outline-secondary btn-sm px-3" onClick={onClose}>
              닫기
            </button>
          </div>

        </div>
      </div>

      {/* ── 확인완료 사유/의견 입력 모달 ── */}
      {showApproveModal && (
        <WorkOrderOpinionModal
          open={showApproveModal}
          mode="input"
          actionType="approve"
          title="처리결과 확인완료 처리"
          zIndex={10060}
          onClose={() => setShowApproveModal(false)}
          onSubmit={handleApproveWithOpinion}
        />
      )}

      {/* ── 보완요청 입력 모달 ── */}
      {showRejectModal && (
        <WorkOrderOpinionModal
          open={showRejectModal}
          mode="input"
          actionType="reject"
          title="처리결과 보완요청"
          zIndex={10060}
          onClose={() => setShowRejectModal(false)}
          onSubmit={handleRejectWithOpinion}
        />
      )}

      {/* ── 의견확인(조회) 모달 ── */}
      {showOpinionViewModal && (() => {
        const targetReportInWO = currentWorkOrder?.reports?.find(r => r.id === activeReport.id);
        const resolvedOpinions = (activeReport.opinions && activeReport.opinions.length > 0)
          ? activeReport.opinions
          : (targetReportInWO?.opinions && targetReportInWO.opinions.length > 0)
            ? targetReportInWO.opinions
            : (activeReport.review_comment ? [{
                id: 'rep-reject-single',
                action_type: 'reject',
                opinion: activeReport.review_comment,
                created_at: activeReport.reviewed_at || activeReport.updated_at,
                author: { name: '요청자' }
              }] : []);

        return (
          <WorkOrderOpinionModal
            open={showOpinionViewModal}
            mode="view"
            actionType="reject"
            title="처리결과 보완요청 의견 확인"
            zIndex={10060}
            opinions={resolvedOpinions}
            onClose={() => setShowOpinionViewModal(false)}
          />
        );
      })()}

      {/* ── 원문 업무지시 조회 팝업 (투명 배경 서브모달) ── */}
      {viewWorkOrderOpen && (
        <WorkOrderFormModal
          open={viewWorkOrderOpen}
          mode="view"
          workOrderId={workOrderId || report?.work_order_id || report?.workOrder?.id || activeReport?.work_order_id}
          isSubModal={true}
          zIndex={10030}
          onClose={() => setViewWorkOrderOpen(false)}
        />
      )}
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default WorkOrderReportDetailModal;
